import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Users, CreditCard, BarChart, List, Home, MessageSquare, Database, Mail, Clock, Shield } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { BidsManagement } from './BidsManagement';
import { TestBid } from '../TestBid';
import { EmailTest } from './EmailTest';
import { BidDiagnostics } from './BidDiagnostics';
import { InsurerManagement } from './InsurerManagement';
import { PaymentsManagement } from './PaymentsManagement';
import { DatabaseSchema } from './DatabaseSchema';
import { RequestStats } from './RequestStats';
import { OriginalAdminView } from './OriginalAdminView';
import { AdminSupportView } from '../admin/AdminSupportView';

interface AdminTabsProps {
  onExit: () => void;
}

export function AdminTabs({ onExit }: AdminTabsProps) {
  const [activeTab, setActiveTab] = React.useState('bids');
  const [openTickets, setOpenTickets] = useState(0);

  useEffect(() => {
    fetchOpenTickets();
  }, []);

  async function fetchOpenTickets() {
    try {
      const { count } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true })
        .in('status', ['open', 'in_progress']);

      setOpenTickets(count || 0);
    } catch (error) {
      console.error('Error fetching open tickets:', error);
      setOpenTickets(0); // Set to 0 on error instead of leaving undefined
    }
  }

  const handleHomeClick = () => {
    onExit();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Tabs */}
      <div className="bg-white border-b overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2">
            <button
              onClick={handleHomeClick}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <Home className="h-5 w-5 mr-2" />
              Back to Homepage
            </button>
          </div>
          <div className="flex flex-nowrap min-w-max sm:min-w-0 space-x-4 sm:space-x-8 -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('original')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'original'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <List className="h-5 w-5 mr-2" />
              Requests
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'support'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              Support {openTickets > 0 && <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">{openTickets}</span>}
            </button>
            <button
              onClick={() => setActiveTab('bids')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'bids'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <LayoutDashboard className="h-5 w-5 mr-2" />
              Bids Management
            </button>
            <button
              onClick={() => setActiveTab('insurers')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'insurers'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="h-5 w-5 mr-2" />
              Insurer Management
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'payments'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CreditCard className="h-5 w-5 mr-2" />
              Payments
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart className="h-5 w-5 mr-2" />
              Request Stats
            </button>
            <button
              onClick={() => setActiveTab('schema')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'schema'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="h-5 w-5 mr-2" />
              Database Schema
            </button>
            <button
              onClick={() => setActiveTab('test-bid')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'test-bid'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className="h-5 w-5 mr-2" />
              Test Bid
            </button>
            <button
              onClick={() => setActiveTab('diagnostics')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'diagnostics'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Shield className="h-5 w-5 mr-2" />
              Diagnostics
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'email'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Mail className="h-5 w-5 mr-2" />
              Email Test
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'bids' && <BidsManagement />}
        {activeTab === 'insurers' && <InsurerManagement />}
        {activeTab === 'payments' && <PaymentsManagement />}
        {activeTab === 'stats' && <RequestStats />}
        {activeTab === 'original' && <OriginalAdminView onExit={onExit} />}
        {activeTab === 'support' && <AdminSupportView />}
        {activeTab === 'schema' && <DatabaseSchema />}
        {activeTab === 'test-bid' && <TestBid />}
        {activeTab === 'email' && <EmailTest />}
        {activeTab === 'diagnostics' && <BidDiagnostics />}
      </div>
    </div>
  );
}
